import type { StudentDashboardStats } from '@/lib/types'

export const dashboardSkillFields = ['listening', 'reading', 'writing', 'speaking'] as const

export type DashboardSkillField = (typeof dashboardSkillFields)[number]

/**
 * Lifetime averages are the journal the front page claims to show. The latest
 * sitting is only a fallback for a skill that has never been averaged yet.
 *
 * Ticket 427: preferring `latestSectionBands` made a writing-only attempt look
 * like the weakest skill even when lifetime writing was already 5.5+.
 */
export function publishedSkillBand(
  field: DashboardSkillField,
  stats?: Pick<StudentDashboardStats, 'averageSectionBands' | 'latestSectionBands'> | null,
): number | null {
  const lifetime = stats?.averageSectionBands?.[field]
  if (typeof lifetime === 'number') return lifetime
  const latest = stats?.latestSectionBands?.[field]
  return typeof latest === 'number' ? latest : null
}

export function resolveFocusSkill<T extends { field: string; band: number | null }>(
  skills: readonly T[],
  sectionFocus?: readonly string[] | null,
): T | null {
  const scored = skills.filter((skill) => typeof skill.band === 'number')
  if (scored.length > 0) {
    const lowest = Math.min(...scored.map((skill) => Number(skill.band)))
    const weak = [...scored]
      .filter((skill) => Number(skill.band) <= lowest + 0.5)
      .sort((left, right) => Number(left.band) - Number(right.band))
    const recommended = (sectionFocus ?? []).map((entry) => entry.toLowerCase())
    // Ticket 427 follow-up: section_focus used to be the recommended *test's
    // types*, so a Writing+Reading paper highlighted Writing even when Reading
    // was a full band lower. Honour the recommendation only when that skill is
    // actually among the weakest published bands.
    return weak.find((skill) => recommended.includes(skill.field)) ?? weak[0] ?? null
  }
  const recommendedField = sectionFocus
    ?.map((entry) => entry.toLowerCase())
    .find((entry) => skills.some((skill) => skill.field === entry))
  return skills.find((skill) => skill.field === recommendedField) ?? null
}
